/**
 * Hangman - Microgame Page
 *
 * Classic word-guessing game. Server selects a word; player guesses letters.
 * Each wrong guess adds a body part to the hangman SVG.
 * FP earned on solve; streak tracking for bonus rewards.
 *
 * All game state lives server-side — the client sends letter guesses,
 * never the word.
 *
 * Features:
 * - 3 difficulty levels: Easy (16 lives), Medium (12 lives), Hard (6 lives)
 * - SVG hangman with progressive drawing (gallows → body → extras)
 * - On-screen A-Z keyboard + physical keyboard input
 * - FP award on win with streak tracking
 * - End-of-game overlay for win/loss
 */

import { logger } from '../lib/logger';
import { showError, showInfo } from '../lib/toast';
import { showResumeBanner } from '../lib/resume-banner';
import { balanceService } from '../services/balance-service';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { showEndOfGameOverlay, buildSoloWinConfig, buildSoloBustConfig, type NextGameSuggestions } from '../components/end-of-game-overlay';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { fireAndForgetCelebration, delayEffect } from '../lib/animation-effects';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';
import { UserAPI } from '../utils/user-api';
import {
  updateHangmanFigure,
  playWinAnimation,
  resetFigure,
  prepareFigure,
  type HangmanDifficulty,
} from '../components/hangman-figure';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Difficulty {
  id: string;
  label: string;
  word_count: number;
  fp: number;
  max_wrong: number;
}

interface DifficultiesResponse {
  enabled: boolean;
  guest_play: boolean;
  difficulties: Difficulty[];
  categories: string[];
}

interface StartResponse {
  round_id: string;
  word_length: number;
  masked_word: string;
  difficulty: string;
  max_wrong: number;
  wrong_count: number;
  guessed_letters: string[];
  status: string;
}

interface GuessResponse {
  round_id: string;
  masked_word: string;
  guessed_letters: string[];
  wrong_count: number;
  correct: boolean;
  status: 'active' | 'won' | 'lost';
  fp_awarded?: number;
  streak?: number;
  word?: string;
  new_balance?: number;
  play_event_id?: string;
  next_game_suggestions?: NextGameSuggestions;
}

interface StatsResponse {
  total_games: number;
  wins: number;
  losses: number;
  win_rate: number;
  current_streak: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = '/api/games/hangman';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let difficulties: Difficulty[] = [];
let selectedDifficulty: string = '';
let selectedCategory: string = '';  // '' means all categories
let currentRoundId: string = '';
let currentMaskedWord: string = '';
let wrongCount = 0;
let maxWrong = 6;
let guessedLetters: Set<string> = new Set();
let gameActive = false;
let isProcessing = false;
let isGuest = false;
let pendingEntryContext: string | undefined;
let eogHandle: SequenceHandle | undefined;

// Cached DOM elements
let selectionSection: HTMLElement;
let gameArea: HTMLElement;
let difficultyCards: HTMLElement;
let maskedWordDisplay: HTMLElement;
let keyboard: HTMLElement;
let wrongCountLabel: HTMLElement;
let gameInfoLabel: HTMLElement;
let hangmanSvg: HTMLElement;

// ---------------------------------------------------------------------------
// Animation Tier (shared component)
// ---------------------------------------------------------------------------

function initHangmanAnimationsModal(): void {
  initAnimationsModal({
    gamePrefix: 'hangman',
    cssClassPrefix: 'hangman-anim',
    pageElementId: 'hangmanPage',
  });
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  logger.info('[Hangman] Initializing');

  selectionSection = document.getElementById('selectionSection')!;
  gameArea = document.getElementById('gameArea')!;
  difficultyCards = document.getElementById('difficultyCards')!;
  maskedWordDisplay = document.getElementById('maskedWordDisplay')!;
  keyboard = document.getElementById('keyboard')!;
  wrongCountLabel = document.getElementById('wrongCountLabel')!;
  gameInfoLabel = document.getElementById('gameInfoLabel')!;
  hangmanSvg = document.getElementById('hangmanSvg')!;

  if (!selectionSection || !gameArea) {
    logger.error('[Hangman] Missing required DOM elements');
    return;
  }

  // Initialize Bootstrap tooltip on info button
  const infoBtn = document.getElementById('infoBtn');
  if (infoBtn && typeof bootstrap !== 'undefined') {
    new bootstrap.Tooltip(infoBtn);
  }

  // Event delegation on difficulty cards
  difficultyCards.addEventListener('click', handleDifficultyClick);

  // Event delegation on keyboard
  keyboard.addEventListener('click', handleKeyboardClick);

  // New game button
  const newGameBtn = document.getElementById('newGameBtn');
  if (newGameBtn) {
    newGameBtn.addEventListener('click', showSelection);
  }

  // Physical keyboard listener
  document.addEventListener('keydown', handleKeydown);

  // Initialize animation modal + tier class
  initHangmanAnimationsModal();

  // Load difficulties
  loadDifficulties();

  // Load guest status, then stats (must await guest check first)
  loadGuestStatus().then(() => loadStats());

  // Check for active round to resume (must complete before auto-start)
  const hasActiveRound = await checkActiveRound();

  // Init celebration particle system
  safeInitCelebrations();

  // Read URL params for guided play flow
  const urlParams = new URLSearchParams(window.location.search);
  const ecParam = urlParams.get('ec');
  const difficultyParam = urlParams.get('difficulty');
  if (ecParam) {
    pendingEntryContext = ecParam;
  }
  if (urlParams.has('ec') || urlParams.has('difficulty')) {
    history.replaceState({}, '', window.location.pathname);
  }

  // Auto-start only if no active round is being resumed
  if (!hasActiveRound && difficultyParam) {
    let resolvedDifficulty: string;
    if (difficultyParam === 'auto') {
      // Try saved preference, fall back to 'easy'
      const saved = await UserAPI.getPreference('hangman_difficulty').catch(() => null);
      resolvedDifficulty = (typeof saved === 'string' && saved) ? saved : 'easy';
    } else {
      resolvedDifficulty = difficultyParam;
    }
    selectedDifficulty = resolvedDifficulty;
    void startGame(resolvedDifficulty);
  }
}

// ---------------------------------------------------------------------------
// API Calls
// ---------------------------------------------------------------------------

async function loadDifficulties(): Promise<void> {
  try {
    const resp = await fetch('/api/games/hangman/categories');
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data: DifficultiesResponse = await resp.json();
    difficulties = data.difficulties;
    renderDifficultyCards(difficulties);
    if (data.categories && data.categories.length > 0) {
      renderCategoryChips(data.categories);
    }
    logger.info(`[Hangman] Loaded ${difficulties.length} difficulties, ${(data.categories || []).length} categories`);
  } catch (err) {
    logger.error('[Hangman] Failed to load difficulties', err);
    showError('Could not load game — try refreshing the page.');
  }
}

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info(`[Hangman] Guest status: ${isGuest}`);
}

async function startGame(difficulty: string): Promise<void> {
  if (isProcessing) return;
  isProcessing = true;

  // Cancel any pending end-of-game sequence
  eogHandle?.cancel();
  eogHandle = undefined;

  // Consume pending entry context (one-shot)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  try {
    const body: Record<string, string> = { difficulty };
    if (selectedCategory) body.category = selectedCategory;
    if (ec) body.entry_context = ec;

    const resp = await fetch('/api/games/hangman/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data: StartResponse = await resp.json();
    currentRoundId = data.round_id;
    currentMaskedWord = data.masked_word;
    wrongCount = data.wrong_count;
    maxWrong = data.max_wrong;
    guessedLetters = new Set(data.guessed_letters);
    gameActive = true;
    window.__versionGuard?.setSafeToReload(false);

    showGameArea(difficulty);
    prepareFigure(hangmanSvg, difficulty as HangmanDifficulty);
    renderWord(data.masked_word);
    renderKeyboard();
    updateHangmanFigure(hangmanSvg, 0);
    updateWrongCount(0);
    loadStats();

    // Save difficulty preference (authenticated users only)
    if (!isGuest) {
      UserAPI.setPreference('hangman_difficulty', difficulty).catch(() => {});
    }

    logger.info(`[Hangman] Round started: ${data.round_id}, word length: ${data.word_length}`);
  } catch (err) {
    logger.error('[Hangman] Failed to start game', err);
    showError('Could not start game — try again.');
  } finally {
    isProcessing = false;
  }
}

async function submitGuess(letter: string): Promise<void> {
  if (!gameActive || isProcessing || !currentRoundId) return;
  if (guessedLetters.has(letter)) {
    showInfo('Already guessed that letter');
    return;
  }

  isProcessing = true;
  // Optimistically disable the key
  disableKey(letter);

  try {
    const resp = await fetch('/api/games/hangman/guess', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ round_id: currentRoundId, letter }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      if (resp.status === 409) {
        // Round already finished — refresh state
        gameActive = false;
        return;
      }
      throw new Error(err.error || `HTTP ${resp.status}`);
    }

    const data: GuessResponse = await resp.json();
    guessedLetters = new Set(data.guessed_letters);
    wrongCount = data.wrong_count;
    currentMaskedWord = data.masked_word;

    // Update UI
    renderWord(data.masked_word);
    updateKeyState(letter, data.correct);
    updateWrongCount(data.wrong_count);
    updateHangmanFigure(hangmanSvg, data.wrong_count);

    // Handle game end
    if (data.status === 'won') {
      gameActive = false;
      handleWin(data);
    } else if (data.status === 'lost') {
      gameActive = false;
      handleLoss(data);
    }
  } catch (err) {
    logger.error('[Hangman] Guess failed', err);
    showError('Guess failed — try again.');
    // Re-enable the key on failure
    enableKey(letter);
  } finally {
    isProcessing = false;
  }
}

async function loadStats(): Promise<void> {
  // Show guest placeholder if not authenticated
  if (isGuest) {
    for (const cardId of ['statsCard', 'selectionStatsCard']) {
      const card = document.getElementById(cardId);
      if (!card) continue;
      const cardBody = card.querySelector('.card-body');
      if (cardBody) {
        cardBody.innerHTML = `
          <h6 class="text-muted mb-3">Your Stats</h6>
          <div class="text-center py-3" data-testid="guest-stats-placeholder">
            <p class="mb-1 text-muted">Your game stats will appear here</p>
            <p class="small text-muted mb-2">Available when signed in</p>
            <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
              Sign in to start tracking →
            </a>
          </div>`;
        const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
        if (signinLink) {
          signinLink.addEventListener('click', (e) => {
            e.preventDefault();
            void fireGuestEvent('tried_locked_feature', true).then((shown) => {
              if (!shown) {
                ensureAuthModalExists();
                authModal.show();
              }
            });
          });
        }
      }
    }
    return;
  }

  try {
    const resp = await fetch('/api/games/hangman/stats');
    if (!resp.ok) return;

    const data: StatsResponse = await resp.json();

    const winRateVal = data.total_games > 0 ? `${data.win_rate}%` : '-';
    const streakVal = data.current_streak > 0 ? `${data.current_streak} 🔥` : '0';
    const gamesVal = `${data.total_games}`;

    // Update both in-game and selection stats cards
    for (const prefix of ['', 'sel']) {
      const winRateEl = document.getElementById(prefix ? 'selStatWinRate' : 'statWinRate');
      const streakEl = document.getElementById(prefix ? 'selStatStreak' : 'statStreak');
      const gamesEl = document.getElementById(prefix ? 'selStatGames' : 'statGames');
      if (winRateEl) winRateEl.textContent = winRateVal;
      if (streakEl) streakEl.textContent = streakVal;
      if (gamesEl) gamesEl.textContent = gamesVal;
    }
  } catch (err) {
    logger.warn('[Hangman] Failed to load stats', err);
  }
}

// ---------------------------------------------------------------------------
// Event Handlers
// ---------------------------------------------------------------------------

function handleDifficultyClick(e: Event): void {
  const target = (e.target as HTMLElement).closest('[data-difficulty]') as HTMLElement | null;
  if (!target) return;

  const difficulty = target.dataset.difficulty;
  if (!difficulty) return;

  selectedDifficulty = difficulty;
  startGame(difficulty);
}

function handleKeyboardClick(e: Event): void {
  const target = (e.target as HTMLElement).closest('.hangman-key') as HTMLElement | null;
  if (!target || target.classList.contains('used')) return;

  const letter = target.dataset.letter;
  if (letter) {
    submitGuess(letter);
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (!gameActive) return;
  // Ignore if user is typing in an input/textarea
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  const key = e.key.toUpperCase();
  if (key.length === 1 && key >= 'A' && key <= 'Z') {
    e.preventDefault();
    submitGuess(key);
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderCategoryChips(categories: string[]): void {
  const row = document.getElementById('categoryFilterRow');
  const _container = document.getElementById('categoryChips');
  if (!row || !_container) return;
  const chipContainer: HTMLElement = _container;

  const allChips = [{ value: '', label: 'All Words' }]
    .concat(categories.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })));

  const VISIBLE_COUNT = 3;
  const visibleChips = allChips.slice(0, VISIBLE_COUNT);
  const hiddenChips = allChips.slice(VISIBLE_COUNT);
  let expanded = false;

  function render(): void {
    const chipsToShow = expanded ? allChips : visibleChips;
    chipContainer.innerHTML = chipsToShow.map(c =>
      `<button class="gc-chip${c.value === selectedCategory ? ' active' : ''}"
               data-category="${c.value}" data-testid="cat-${c.value || 'all'}">${c.label}</button>`
    ).join('');

    if (hiddenChips.length > 0) {
      const moreLabel = expanded ? 'Less' : 'More ...';
      chipContainer.innerHTML += `<button class="gc-chip" data-testid="cat-more" data-more="true">${moreLabel}</button>`;
    }
  }

  render();

  chipContainer.addEventListener('click', (e) => {
    const chip = (e.target as HTMLElement).closest('.gc-chip') as HTMLElement | null;
    if (!chip) return;

    if (chip.dataset.more) {
      expanded = !expanded;
      render();
      return;
    }

    selectedCategory = chip.dataset.category || '';
    chipContainer.querySelectorAll('.gc-chip').forEach(c => {
      if (!c.hasAttribute('data-more')) c.classList.remove('active');
    });
    chip.classList.add('active');
  });

  row.style.display = '';
}

function renderDifficultyCards(diffs: Difficulty[]): void {
  const TAGLINES: Record<string, string> = {
    easy: 'Relaxed start',
    medium: 'Balanced challenge',
    hard: 'For word masters',
  };
  const ICONS: Record<string, string> = {
    easy: 'bi-emoji-smile',
    medium: 'bi-lightning',
    hard: 'bi-fire',
  };

  difficultyCards.innerHTML = diffs.map(d => `
    <div class="col-12 col-sm-4">
      <div class="card gc-card gc-card-${d.id}" data-difficulty="${d.id}" data-testid="difficulty-${d.id}">
        <div class="card-body text-center py-3">
          <div class="gc-card-icon"><i class="bi ${ICONS[d.id] || 'bi-circle'}"></i></div>
          <div class="gc-card-label">${d.label}</div>
          <div class="gc-card-tagline">${TAGLINES[d.id] || ''}</div>
          <div class="gc-card-detail">${d.max_wrong} guesses · ${d.fp} FP</div>
        </div>
      </div>
    </div>
  `).join('');
}

function renderWord(masked: string, showUnguessed = false): void {
  maskedWordDisplay.innerHTML = '';
  for (const ch of masked) {
    const slot = document.createElement('span');
    slot.className = 'letter-slot';
    if (ch === '_') {
      slot.textContent = '\u00A0'; // non-breaking space
    } else if (!isAlpha(ch)) {
      slot.textContent = ch;
      slot.classList.add('special');
    } else {
      slot.textContent = ch;
      // If showUnguessed, letters not previously guessed get 'unguessed' style
      if (showUnguessed && !guessedLetters.has(ch.toUpperCase())) {
        slot.classList.add('unguessed');
      } else {
        slot.classList.add('revealed');
      }
    }
    maskedWordDisplay.appendChild(slot);
  }
}

function renderKeyboard(): void {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  keyboard.innerHTML = '';
  for (const letter of letters) {
    const btn = document.createElement('button');
    btn.className = 'hangman-key';
    btn.textContent = letter;
    btn.dataset.letter = letter;
    btn.dataset.testid = `key-${letter}`;
    if (guessedLetters.has(letter)) {
      btn.classList.add('used');
      btn.disabled = true;
    }
    keyboard.appendChild(btn);
  }
}

function updateKeyState(letter: string, correct: boolean): void {
  const key = keyboard.querySelector(`[data-letter="${letter}"]`) as HTMLElement | null;
  if (key) {
    key.classList.add('used');
    key.classList.add(correct ? 'correct' : 'wrong');
    (key as HTMLButtonElement).disabled = true;
  }
}

function disableKey(letter: string): void {
  const key = keyboard.querySelector(`[data-letter="${letter}"]`) as HTMLElement | null;
  if (key) {
    key.classList.add('used');
    (key as HTMLButtonElement).disabled = true;
  }
}

function enableKey(letter: string): void {
  const key = keyboard.querySelector(`[data-letter="${letter}"]`) as HTMLElement | null;
  if (key) {
    key.classList.remove('used');
    (key as HTMLButtonElement).disabled = false;
  }
}

function updateWrongCount(count: number): void {
  wrongCountLabel.textContent = `${count} / ${maxWrong} wrong`;
}

// ---------------------------------------------------------------------------
// Game End Handling
// ---------------------------------------------------------------------------

function handleWin(data: GuessResponse): void {
  const fp = data.fp_awarded || 0;
  const streak = data.streak || 0;
  const ngs = data.next_game_suggestions;

  // Refresh navbar FP balance (overlay already shows the reward)
  if (fp > 0) {
    balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
  }

  // Playful win animation: complete the man, turn blue, happy face
  playWinAnimation(hangmanSvg);

  // Build end-of-game config via shared builder
  const config = buildSoloWinConfig({
    game: 'hangman',
    totalScore: 0,
    fpEarned: fp,
    isGuest,
    onPlayAgain: () => { void fireGameCompleted(isGuest); startGame(selectedDifficulty); },
    playAgainUrl: '/games/hangman',
    playEventId: data.play_event_id,
    nextGameSuggestions: ngs,
  });

  // Hangman-specific overrides
  config.message = 'You got it! 🎉';
  config.subtitle = streak >= 5 ? `You're on fire! 🔥 ${streak} in a row!` : (streak > 1 ? `Streak: ${streak} 🔥` : undefined);

  // Non-guest, non-auto-next: offer "Change Difficulty" as secondary
  const hasAutoNext = !!ngs?.auto_next && !isGuest;
  if (!isGuest && !hasAutoNext) {
    config.secondaryAction = { label: 'Change Difficulty', onClick: showSelection, icon: 'bi-grid' };
  }

  // Orchestrated: fire-and-forget celebration → 500ms delay → overlay
  eogHandle?.cancel();
  eogHandle = runSequence('hangman-eog-win', [
    fireAndForgetCelebration('win'),
    delayEffect(500),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
  });
  loadStats();
}

function handleLoss(data: GuessResponse): void {
  const word = data.word || '';
  const ngs = data.next_game_suggestions;

  // Reveal the full word (unguessed letters shown in dark/black)
  renderWord(word, true);

  // Build end-of-game config via shared builder
  const config = buildSoloBustConfig({
    game: 'hangman',
    isGuest,
    onPlayAgain: () => { void fireGameCompleted(isGuest); startGame(selectedDifficulty); },
    playAgainUrl: '/games/hangman',
    playEventId: data.play_event_id,
    nextGameSuggestions: ngs,
  });

  // Hangman-specific overrides
  config.message = `So close! The word was ${word}.`;
  config.subtitle = undefined;

  // Non-guest, non-auto-next: offer "Change Difficulty" as secondary
  const hasAutoNext = !!ngs?.auto_next && !isGuest;
  if (!isGuest && !hasAutoNext) {
    config.secondaryAction = { label: 'Change Difficulty', onClick: showSelection, icon: 'bi-grid' };
  }

  // Orchestrated: 800ms delay (for hanging animation) → overlay
  eogHandle?.cancel();
  eogHandle = runSequence('hangman-eog-loss', [
    delayEffect(800),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
  });
  loadStats();
}

// ---------------------------------------------------------------------------
// View Management
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Resume Active Round
// ---------------------------------------------------------------------------

async function checkActiveRound(): Promise<boolean> {
  try {
    const resp = await fetch(`${API_BASE}/state`, { credentials: 'same-origin' });
    if (!resp.ok) return false;
    const data = await resp.json();
    if (!data.round) return false;

    const round = data.round;
    currentRoundId = round.round_id;
    currentMaskedWord = round.masked_word;
    wrongCount = round.wrong_count;
    maxWrong = round.max_wrong;
    guessedLetters = new Set(round.guessed_letters);
    gameActive = true;
    window.__versionGuard?.setSafeToReload(false);
    selectedDifficulty = round.difficulty;

    showGameArea(round.difficulty);
    prepareFigure(hangmanSvg, round.difficulty as HangmanDifficulty);
    renderWord(round.masked_word);
    renderKeyboard();
    // Restore guessed letter states on keyboard
    for (const letter of round.guessed_letters) {
      updateKeyState(letter, round.masked_word.includes(letter));
    }
    updateHangmanFigure(hangmanSvg, round.wrong_count);
    updateWrongCount(round.wrong_count);

    // Show resume banner
    showResumeBanner({
      container: gameArea,
      onAbandon: async () => {
        try {
          await fetch(`${API_BASE}/abandon`, { method: 'POST' });
        } catch (err) {
          logger.error('Failed to abandon round:', err);
        }
        gameActive = false;
        currentRoundId = '';
        showSelection();
      },
    });

    logger.info(`[Hangman] Resumed active round: ${round.round_id}`);
    return true;
  } catch (err) {
    logger.warn('[Hangman] Failed to check active round', err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// UI State Transitions
// ---------------------------------------------------------------------------

function showGameArea(difficulty: string): void {
  selectionSection.style.display = 'none';
  gameArea.style.display = '';

  // Set info label — include category if filtered
  const catLabel = selectedCategory
    ? ` · ${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}`
    : '';
  gameInfoLabel.textContent = difficulty.charAt(0).toUpperCase() + difficulty.slice(1) + catLabel;
}

function showSelection(): void {
  window.__versionGuard?.setSafeToReload(true);

  // Cancel any pending end-of-game sequence
  eogHandle?.cancel();
  eogHandle = undefined;

  gameArea.style.display = 'none';
  selectionSection.style.display = '';
  gameActive = false;
  currentRoundId = '';
  selectedDifficulty = '';
  // Note: selectedCategory is preserved so the dropdown retains its value

  // Reset figure
  resetFigure(hangmanSvg);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isAlpha(ch: string): boolean {
  return /^[a-zA-Z]$/.test(ch);
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', init);
